import Random from "rung/src/random";
import Tiles from "../tiles";
import Entity from "./entity";
import Directions from "./directions";
import { generateDungeon } from "./dungeon";
import { generateTrainingHall, spawnTargetDrone } from "./training";
import Items from "./items";
import Weapons from "./weapons";
import Armors from "./armors";
import { generateCaves } from "./caves";
import { createFOV } from "./fov";
import { spawnToothrot } from "./characters";
import { spawnTargetDummp } from "./monsters";

const rng = new Random(Math.random);

class Stage {
  constructor(adventure, player) {
    this.adventure = adventure;
    this.player = player;
    this.isWaitingForSchedule = false;
    this.initializeLevelMap(this.adventure.startLevel());
  }

  // Loading a level for the first time
  initializeLevelMap(level, startAt=false) {
    this.lock();

    this.width = level.width;
    this.height = level.height;
    this.levelId = level.id;
    this.map = level.tiles;

    // Create 2D spatial index
    this.initializeEntities();

    // Need entities index initialised before this can run
    try {
      level.populateScene(this);
    } catch(err) {
      console.error(err);
      throw err;
    }

    if (startAt) {
      this.moveEntityTo(this.player, startAt.x, startAt.y, false);
    }

    // Need to initialize this last so we know where the player is
    this.initializeVisibility();

    this.unlock();
  }

  inOverworld() {
    // TODO: 7DRL HACK!!! should use the level data to determine this
    return this.levelId == "Farmlet" || this.levelId == "Forest";
  }

  setWaiting(isWaiting) {
    this.isWaitingForSchedule = isWaiting;
  }

  onExitLevel(listener) {
    this.levelExitListener = listener;
  }

  onEnterLevel(listener) {
    this.levelEnterListener = listener;
  }

  switchLevelMap(id, target) {
    window.stage = this;

    this.levelExitListener(id);
    this.adventure.saveLevel(this.levelId, this.toSnapshot());

    if (this.adventure.hasVisited(id)) {
      this.restoreLevelMap(this.adventure.restoreLevel(id), target);
    } else {
      this.initializeLevelMap(this.adventure.spawnLevel(id), target);
    }

    this.levelEnterListener(id);
  }

  findOpenTile() {
    let result = false
    const xMax = this.width - 6;
    const yMax = this.height - 6;

    while (!result) {
      const cx = rng.integer(3, xMax);
      const cy = rng.integer(3, yMax);

      if (!this.map[cy][cx].blocking) {
        result = { x: cx, y: cy };
      }
    }

    return result;
  }

  movementCost(x, y) {
    return this.isUnoccupied(x, y) ? 1 : 5;
  }

  adjacentPoints(x, y) {
    const points = [];
    for (let direction of Directions.CARDINAL) {
      let candidate = {
        x: x + direction.x,
        y: y + direction.y
      };
      if (
        candidate.x >= 0 &&
        candidate.x < this.width &&
        candidate.y >= 0 &&
        candidate.y < this.height
      ) {
        points.push(candidate);
      }
    }
    return points;
  }

  isOf(x, y, tileType) {
    return this.map[y][x].name == tileType.name;
  }

  isAdjacentTo(x, y, tileType) {
    return this.adjacentPoints(x, y).some(p => this.map[p.y][p.x].name == tileType.name);
  }

  adjacentTilesOf(x, y, tileType) {
    return this.adjacentPoints(x, y).filter(p => this.map[p.y][p.x].name == tileType.name);
  }

  adjacentOpenTiles(x, y) {
    return this.adjacentPoints(x, y).filter(p => this.canMoveTo(p.y, p.y));
  }

  canCharacterMoveTo(x, y) {
    try {
      const blocking = !this.map[y][x].blocking;
      return blocking;
    } catch(err) {
      console.log(err);
      return false;
    }
  }

  canInteractWith(x, y) {
    return this.isWithinBounds(x, y) ? this.map[y][x].isOpenable : false;
  }

  canMoveTo(x, y) {
    return this.isWithinBounds(x, y) ? !this.map[y][x].blocking : false;
  }

  isWithinBounds(x, y) {
    return y >= 0 && y < this.height && x >= 0 && x < this.width;
  }

  isPartiallyCovered(x, y) {
    return this.map[y][x].blocking || !this.isUnoccupied(x, y);
  }

  // TODO: sort this out with a better tile representation
  isShotCovered(x, y) {
    return (this.map[y][x].blocking && this.map[y][x].opaque) || !this.isUnoccupied(x, y);
  }

  isUnoccupied(x, y) {
    return !this.entitiesMap[y][x].some(e => e.isBlocking())
  }

  entitiesAt(x, y) {
    return this.entitiesMap[y][x];
  }

  visibleEntities() {
    return this.entities.filter(en => this.isVisible(en.x, en.y) && !en.isPlayer());
  }

  lookAt(x, y) {
    const entities = this.entitiesAt(x, y);
    if (entities.length > 0) {
      // TODO: sort by priority
      return entities[entities.length - 1];
    } else {
      return this.map[y][x];
    }
  }

  isOpaque(x, y) {
    try {
      return this.map[y][x].opaque;
    } catch(err) {
      console.log(x, y);
      console.log(this.map);
      throw err;
    }
  }

  isVisible(x, y) {
    return this.visible.has(`${x},${y}`);
  }

  isSeen(x, y) {
    return this.seen.has(`${x},${y}`);
  }

  openDoor(x, y) {
    const openTile = this.map[y][x].openTile;
    if (openTile) {
      this.map[y][x] = Tiles[openTile];
    }
    this.refreshVisibility();
  }

  revealTile(x, y) {
    const id = `${x},${y}`;
    this.visible.add(id);
    this.seen.add(id);
  }

  initializeVisibility(seen=false) {
    this.visible = new Set();
    this.seen = seen ? seen : new Set();
    this.refreshFOV = createFOV(
      this.width,
      this.height,
      (x, y) => this.revealTile(x, y),
      (x, y) => this.isOpaque(x, y)
    );
    this.refreshVisibility();
  }

  refreshVisibility() {
    const fovRadius = this.inOverworld() ? 20 : 12;
    this.visible.clear();
    this.refreshFOV(this.player.x, this.player.y, fovRadius);
  }

  initializeEntities() {
    this.entities = [];
    this.entitiesMap = Array(this.height)
      .fill(null)
      .map(() =>
        Array(this.width)
          .fill(null)
          .map(() => [])
      );
  }

  spawnEntity(entity, x = false, y = false) {
    if (x && y) {
      entity.x = x;
      entity.y = y;
    } else {
      const point = this.findOpenTile();
      entity.x = point.x;
      entity.y = point.y;
    }
    this.addEntity(entity);
  }

  addEntity(entity) {
    this.entities.push(entity);
    this.entitiesMap[entity.y][entity.x].push(entity);
  }

  moveEntityTo(entity, x, y, isInLevel=true) {
    let tx = x;
    let ty = y;

    // if (entity.isPlayer() && this.map[y][x].isTeleporter) {
    //   tx = this.map[y][x].teleportX;
    //   ty = this.map[y][x].teleportY;
    // }

    if (isInLevel && entity.isPlayer() && this.map[y][x].level) {
      this.removeEntity(entity);
      this.switchLevelMap(this.map[y][x].level, this.map[y][x].target);
      return;
    }

    this.entitiesMap[entity.y][entity.x] = this.entitiesMap[entity.y][
      entity.x
    ].filter(e => e !== entity);
    entity.x = tx;
    entity.y = ty;
    this.entitiesMap[ty][tx].push(entity);
  }

  removeEntity(entity) {
    this.entitiesMap[entity.y][entity.x] = this.entitiesMap[entity.y][
      entity.x
    ].filter(e => e !== entity);
    this.entities = this.entities.filter(e => e !== entity);
  }

  toSnapshot() {
    return {
      id: this.levelId,
      width: this.width,
      height: this.height,
      entities: this.entities,
      entitiesMap: this.entitiesMap,
      seen: this.seen,
      map: this.map
    }
  }

  lock() {
    this.locked = true;
  }

  unlock() {
    this.locked = false;
  }

  isLocked() {
    return this.locked;
  }

  restoreLevelMap(snapshot, startAt) {
    this.lock();
    const level = this.adventure.getLevel(snapshot.id);

    console.log("restoreLevelMap level")

    this.levelId = snapshot.id;
    this.width = snapshot.width;
    this.height = snapshot.height;
    this.entities = snapshot.entities;
    this.entitiesMap = snapshot.entitiesMap;
    //this.seen = snapshot.seen;
    this.map = snapshot.map;

    console.log("restoreLevelMap snapshot reset")

    let target = level.portals.entrance ? level.portals.entrance : level.portals.start;

    console.log(level.portals)

    this.spawnEntity(this.player, startAt.x, startAt.y);

    console.log("portal fuckery")

    this.initializeVisibility(snapshot.seen);

    this.unlock();
  }
}

export default Stage;
